import React, { useCallback, useEffect, useState, useRef } from 'react'
import { LineChart, AreaChart } from 'react-chartkick'
import { Label, Button, Modal, Checkbox, DropDownInput, Input } from '../../util/ui'
import { MultiSelect, DropDown } from '../../util/ui_components'

import { useDispatch, useSelector } from 'react-redux';
import { loadScript, useSimpleForm, useToggleGroup, fetchByJSON, useCachedData, toUnicode } from '../../util/util'
// import {TooltipComponent} from 'echarts/components';

import AreaGraph from './areaGraph'
import BarChart from './barChart'
import BoxPlot from './boxPlot'
import LineGraph from './lineGraph'
import Histogram from './histogram'
import Heatmap from './heatmap'
import PieChart from './pieChart'
import ScatterPlot from './scatterPlot'
import FacetedScatterPlot from './facetedScatterPlot'
import { ChromePicker } from 'react-color';
import { thebeConfigR } from '../../util/thebeConfigs';
import { usePageKernel, attachCells } from '../../util/thebeSession';
import sanitizeJsonString from '../common/sanitize';
import './index.css'
import { InlineTip } from '../common/tip';
//

// const reconnectErrorFunction = () => {
//   alert('Kernel is taking too much time. Please reduce the data size!')
// }

const reconnectErrorFunction = () => {
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: transparent;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 48px;
    height: 48px;
    border: 5px solid rgba(63, 91, 203, 0.84);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  `;

  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
  `;

  document.head.appendChild(style);
  overlay.appendChild(spinner);
  document.body.appendChild(overlay);

  setTimeout(() => {
    overlay.remove();
    style.remove();
  }, 1000);
};


const Graphs = [
    AreaGraph,
    BarChart,
    BoxPlot,
    Histogram,
    LineGraph,
    PieChart,
    Heatmap,
    ScatterPlot,
    FacetedScatterPlot
]

const Functions = [
    'Distribution',
    'Range',
    'Patterns',
    'Change over Time',
    'Comparisons',
    'Proportions',
    'Relationships',
    '3D'
]

const PLOTS = {}

let GraphOptionViews = {}, GraphConfigs = {}

for (let e of Graphs) {
    PLOTS[e.config.name] = {
        function: e.config.function
    }
    GraphConfigs[e.config.name] = e.config
    GraphOptionViews[e.config.name] = e.view
}

const initialCode = data => `
library(jsonlite)
data_json <- '${data}'
# Convert JSON to a list. pandas' df.to_json() omits the row-index key entirely for any
# NaN cell (rather than including it as null), so columns with missing values end up with
# fewer entries than columns with none -- reindex every column against the union of all
# row indices first, so missing cells become proper NA in the right position, before
# combining into a data frame.
df_list <- fromJSON(data_json)
all_indices <- unique(unlist(lapply(df_list, names)))
all_indices <- all_indices[order(as.numeric(all_indices))]
columns_list <- lapply(df_list, function(col) {
    col_names <- names(col)
    sapply(all_indices, function(idx) {
        if (idx %in% col_names) {
            val <- col[[idx]]
            if (is.null(val)) NA else val
        } else {
            NA
        }
    })
})
names(columns_list) <- names(df_list)

# Combine the list into a proper data frame
df_r <- as.data.frame(columns_list, stringsAsFactors = FALSE, check.names = FALSE)
df_r <- type.convert(df_r, as.is = TRUE)
`


// const initialCode = data => `
// install.packages(c("jsonlite", "ggplot2", "plotly", "base64", "tidyr", "tibble", "dplyr"))
// library(jsonlite)
// library(ggplot2)
// library(plotly)
// library(base64)
// library(dplyr)

// data_json <- "${btoa(data)}" # Escape double quotes
// json_data <- fromJSON(rawToChar(base64_dec(data_json)))
// # Extract all keys (assuming they represent row numbers) and find the unique set
// all_keys <- unique(unlist(lapply(json_data, names)))

// # Sort the keys to maintain the original order
// all_keys_sorted <- sort(as.numeric(all_keys))

// # Initialize an empty list for columns, ensuring it will have the correct length
// columns_list <- setNames(vector("list", length(json_data)), names(json_data))

// # Fill each column, aligning according to the sorted keys, inserting NA where values are missing
// for(column_name in names(json_data)) {
//   column_data <- unlist(json_data[[column_name]])
//   # Create a full-length vector filled with NA of appropriate type
//   full_column <- vector("character", length(all_keys_sorted))
//   names(full_column) <- as.character(all_keys_sorted)
  
//   # Replace NAs with actual data where available
//   full_column[names(column_data)] <- column_data
  
//   # Assign the processed column to the list
//   columns_list[[column_name]] <- full_column
// }

// # Convert the list to a data.frame
// df_r <- as.data.frame(columns_list, stringsAsFactors = FALSE)

// # Convert character columns that are numeric
// df_r <- type.convert(df_r, as.is = TRUE)
// `


export default function ({ location }) {
    useCachedData()
    const [plots, setPlots] = useState(Object.keys(PLOTS))
    const [optionsVisible, showOptions] = useState(0)
    let { ref, hide: hideSelections } = useToggleGroup()
    const [currentPlot, setCurrentPlot] = useState('')
    const [code, setCode] = useState('')
    const [activateStatus, setActivateStatus] = useState('Loading...')
    let [dfJSON, setDfJSON] = useState('')//dataframe json
    let dataset = useSelector(state => state.dataset)
    let { result, getData } = useSimpleForm()
    let codeParent = useRef()
    let kernelRef = useRef()
    // one kernel per mounted page, shut down again on unmount
    const getPageKernel = usePageKernel(thebeConfigR)
    let [guideStep, setGuideStep] = useState(0)
    let [image, setImage] = useState('')

    useEffect(() => {
        switch (guideStep) {
            case 0:
                if (location?.state?.guide) {
                    setGuideStep(1)
                }
                break
            case 1:
                document.querySelector('#functionSelection')?.classList.add('btn-blink')
                document.querySelector('#functionSelection+div div:nth-child(5)')?.classList.add('btn-inner-blink')
                break
            case 2:
                document.querySelector('#functionSelection')?.classList.remove('btn-blink')
                document.querySelector('#functionSelection+div div:nth-child(5)')?.classList.remove('btn-blink')
                document.querySelector('#graphTypeSelection')?.classList.add('btn-blink')
                document.querySelector('#graphTypeSelection+div div:nth-child(5)')?.classList.add('btn-inner-blink')
                break
            case 3:
                document.querySelector('#graphTypeSelection')?.classList.remove('btn-blink')
                document.querySelector('#graphTypeSelection+div div:nth-child(5)')?.classList.remove('btn-inner-blink')
                document.querySelector('#optionsButton')?.classList.add('btn-blink')
                break
            case 4:
                document.querySelector('#optionsButton')?.classList.remove('btn-blink')
                document.querySelectorAll('#visModal>div>div:nth-child(2) .dropdown button')[0]?.classList.add('btn-blink')
                document.querySelectorAll('#visModal>div>div:nth-child(2) .dropdown ul li:nth-child(3)')[0]?.classList.add('btn-inner-blink')
                break
            case 5:
                document.querySelectorAll('#visModal>div>div:nth-child(2) .dropdown button')[0]?.classList.remove('btn-blink')
                document.querySelectorAll('#visModal>div>div:nth-child(2) .dropdown ul li:nth-child(3)')[0]?.classList.remove('btn-inner-blink')
                document.querySelectorAll('#visModal>div>div:nth-child(2) .dropdown button')[1]?.classList.add('btn-blink')
                document.querySelectorAll('#visModal>div>div:nth-child(2) .dropdown ul li:nth-child(2)')[1]?.classList.add('btn-inner-blink')
                break
            case 6:
                document.querySelectorAll('#visModal>div>div:nth-child(2) .dropdown button')[1]?.classList.remove('btn-blink')
                document.querySelectorAll('#visModal>div>div:nth-child(2) .dropdown ul li:nth-child(2)')[1]?.classList.remove('btn-inner-blink')
                document.querySelector('#scatterPlotConfirm')?.classList.add('btn-blink')
                break
            case 7:
                document.querySelector('#scatterPlotConfirm')?.classList.remove('btn-blink')
                document.querySelector('#runButton')?.classList.add('btn-blink')
                break
            case 8:
                document.querySelector('#runButton')?.classList.remove('btn-blink')
                break
        }
    }, [guideStep])

    //Not just rerun the current code
    //It's reinject the data and rerun the current code
    const runCode = async (e) => {
        try {
            setActivateStatus('Running Initial Code ...');
        let res = await fetchByJSON('current_data_json', {
            filename: dataset.filename
        })

        let json = await res.json();
        let json_string = sanitizeJsonString(json.data);
        // let res2 = await kernelRef.current.requestExecute({ code:initialCode(option,dfJSON.current) }).done
        let res2 = await kernelRef.current.requestExecute({ code:initialCode(json_string) }).done
        
        if(guideStep == 7) setGuideStep(8)
        document.querySelector('.thebelab-run-button').click()
        setActivateStatus('Ready');
        } catch (err) {
            console.error("Error during run:", err);
            setActivateStatus('Error during run');
        }
    }

    useEffect(() => {
        reconnectErrorFunction();
        if (!code) return
        codeParent.current.innerHTML = ''
        let pre = document.createElement('pre')
        pre.setAttribute('data-executable', 'true')
        pre.setAttribute('data-language', 'R')
        codeParent.current.appendChild(pre)
        pre.innerHTML = code
        let isActive = true

        // Only the freshly created <pre> needs rendering here -- bootstrap() would also
        // request another kernel, and thebe's "status" bus is global, so the handler this
        // used to register kept firing (for every page's kernel) after unmount.
        getPageKernel().then((kernel) => {
            if (!isActive) return
            kernelRef.current = kernel
            kernelRef.current._reconnect = reconnectErrorFunction;
            attachCells(kernel)
        }).catch((err) => console.error('Error connecting to the R kernel:', err))

        return () => { isActive = false }
    }, [code])

    //start thebelab automatically
    //load current dataframe
    useEffect(() => {
        reconnectErrorFunction();
        if (!dataset.filename) {
            setActivateStatus('No data')
            return
        }

        let isActive = true

        //execute code in advance on thebelab to import libraries and set dataframe variable
        getPageKernel().then(async (kernel) => {
            if (!isActive) return
            let res = await fetchByJSON('current_data_json', {
                filename: dataset.filename
            })

            let g = await res.json()
            if (!isActive) return
            kernelRef.current = kernel
            kernelRef.current._reconnect = reconnectErrorFunction;
            let json_string = sanitizeJsonString(g.data);
            setDfJSON(json_string)
            setActivateStatus('Ready')
        }).catch((err) => {
            if (!isActive) return
            console.error('Error connecting to the R kernel:', err)
            setActivateStatus('Error connecting to R')
        })

        return () => { isActive = false }
    }, [dataset.filename])


    const setPlotsByFunctions = useCallback((functions) => {
        if (!functions || functions.length === 0) {
            setPlots(Object.keys(PLOTS))
            return
        }

        const res = new Set()
        for (let key in PLOTS) {
            for (let func of PLOTS[key].function) {
                let selected = 0
                for (let selectedFunc of functions) {
                    if (func.indexOf(selectedFunc) > -1) {
                        selected = 1
                        break
                    }
                }
                if (selected) {
                    res.add(key)
                }
            }
        }

        setPlots([...res])
    }, [setPlots])

    let OptionView = GraphOptionViews[currentPlot]

    return (<div className='flex flex-col items-center w-full bg-gray-100' style={{ height: 'calc(100% - 0rem)' }}>
        <Modal id="visModal" fixedModalPosition={{
            left: '20vw',
            top: '10vh',
            width: 'fit-content'
        }} zIndex={11} isOpen={optionsVisible} setIsOpen={showOptions} onClose={() => {
            showOptions(0)
            // setCode(GraphConfigs[currentPlot].getCode(result), dataset)
        }}>
            {
                GraphOptionViews[currentPlot] ? <OptionView guideStep={guideStep} setGuideStep={setGuideStep} setCode={setCode} /*setChartData={setChartData}*/ setImage={setImage} dataset={dataset} result={result} showOptions={showOptions} /> : ''
            }
        </Modal>

        <div className='flex justify-between items-center w-full h-20 box-border py-2 px-4 shadow-md' style={{ zIndex: 10 }}>
            <div className='w-72 px-1'>
                <MultiSelect id="functionSelection" ref={ref} defaultText='Select graph property' selections={Functions} onSelect={e => {
                    setPlotsByFunctions(e)
                    if (guideStep == 1) setGuideStep(2)
                }} customStyle={{ fontSize: 'medium' }} />
            </div>
            <div className='w-72 px-1'>
                <DropDown id="graphTypeSelection" ref={ref} defaultText={'Select Graph Type'} width={'w-72'} items={plots} onSelect={e => {
                    setCurrentPlot(e)
                    if (guideStep == 2) setGuideStep(3)
                }} />
            </div>
            {/* <div className='w-auto flex justify-center items-center px-1'>
                <InlineTip info='<Type description>' />
            </div> */}
            <div className='w-auto flex justify-center items-center px-1'>
                <div className={``}>{activateStatus}</div>
                <InlineTip zIndex={10} info='The loading status of a remote environment, R code will be executed in that environment as soon as it is ready.' />
            </div>
            <div className='w-72 px-1'>
                <Button id="optionsButton" hasPadding={false} disabled={!currentPlot} text="Options" overrideClass={`w-full rounded font-semibold border focus:outline-none h-10  ${!currentPlot ? 'text-gray-400 cursor-default' : 'text-black cursor-pointer'}`} onClick={e => { 
                    if (currentPlot) showOptions(1) 
                    if (guideStep == 3) setGuideStep(4)
                }} hoverAnimation={false} />
            </div>
            <div className='w-72 px-1'>
                <Button id="runButton" hasPadding={false} disabled={!code} text="Confirm" overrideClass={`w-full rounded font-semibold border focus:outline-none h-10 text-black cursor-pointer ${!code
                    ? 'text-gray-400 cursor-default' : 'text-black cursor-pointer'}`} onClick={runCode} hoverAnimation={false} />
            </div>
        </div>
        <div className='flex-grow-1 w-full' ref={codeParent}>
            {code ? '' : <div className='w-full flex-grow-0 h-48 flex justify-center items-center text-gray-500 font-semibold'>
                Select a plot type to see the corresponding code in R
            </div>}
        </div>
    </div>)
}